import { useEffect } from 'react';

export interface PageMeta {
  title: string;
  description: string;
  canonical?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  jsonLd?: Record<string, unknown>;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  articleSection?: string;
  articleTags?: string[];
}

function setMeta(selector: string, attr: 'name' | 'property', key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Manages <head> tags for the current route — title, meta description,
 * canonical, Open Graph, Twitter card, and JSON-LD structured data.
 * Note: client-side only — for production SEO, swap for SSR/prerender.
 */
export function usePageMeta(meta: PageMeta) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = meta.title;

    setMeta('meta[name="description"]', 'name', 'description', meta.description);

    if (meta.canonical) setLink('canonical', meta.canonical);

    setMeta('meta[property="og:title"]', 'property', 'og:title', meta.title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', meta.description);
    setMeta('meta[property="og:type"]', 'property', 'og:type', meta.ogType ?? 'website');
    if (meta.canonical) setMeta('meta[property="og:url"]', 'property', 'og:url', meta.canonical);
    if (meta.ogImage) setMeta('meta[property="og:image"]', 'property', 'og:image', meta.ogImage);
    setMeta('meta[property="og:site_name"]', 'property', 'og:site_name', 'StoryMint');

    setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', meta.twitterCard ?? 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', meta.title);
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', meta.description);
    if (meta.ogImage) setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', meta.ogImage);

    if (meta.articlePublishedTime) {
      setMeta(
        'meta[property="article:published_time"]',
        'property',
        'article:published_time',
        meta.articlePublishedTime,
      );
    }
    if (meta.articleModifiedTime) {
      setMeta(
        'meta[property="article:modified_time"]',
        'property',
        'article:modified_time',
        meta.articleModifiedTime,
      );
    }
    if (meta.articleAuthor) {
      setMeta('meta[property="article:author"]', 'property', 'article:author', meta.articleAuthor);
    }
    if (meta.articleSection) {
      setMeta('meta[property="article:section"]', 'property', 'article:section', meta.articleSection);
    }

    // Remove old article:tag entries, then add fresh ones
    document.head.querySelectorAll('meta[property="article:tag"]').forEach((n) => n.remove());
    if (meta.articleTags?.length) {
      meta.articleTags.forEach((tag) => {
        const el = document.createElement('meta');
        el.setAttribute('property', 'article:tag');
        el.setAttribute('content', tag);
        document.head.appendChild(el);
      });
    }

    // JSON-LD structured data — single script tag, replaced per page
    const jsonLdId = 'page-jsonld';
    document.getElementById(jsonLdId)?.remove();
    if (meta.jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = jsonLdId;
      script.textContent = JSON.stringify(meta.jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      document.title = prevTitle;
      document.getElementById(jsonLdId)?.remove();
      document.head.querySelectorAll('meta[property="article:tag"]').forEach((n) => n.remove());
    };
  }, [meta]);
}
